import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Clock,
  ShieldAlert,
  TrendingDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ComplianceAlert } from '@/types/compliance';

interface AlertStatsProps {
  alerts: ComplianceAlert[];
}

export function AlertStats({ alerts }: AlertStatsProps) {
  const stats = {
    total: alerts.length,
    open: alerts.filter(a => a.status === 'open').length,
    acknowledged: alerts.filter(a => a.status === 'acknowledged').length,
    resolved: alerts.filter(a => a.status === 'resolved').length,
    critical: alerts.filter(a => a.severity === 'critical' && a.status !== 'resolved').length,
    high: alerts.filter(a => a.severity === 'high' && a.status !== 'resolved').length,
    slaBreached: alerts.filter(a => a.sla_breach && a.status !== 'resolved').length,
  };

  const pendingCount = stats.open + stats.acknowledged;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
            <AlertCircle className="h-6 w-6 text-blue-500" />
          </div>
        </CardContent>
      </Card>

      <Card className={cn(
        "border-l-4",
        stats.open > 0 ? "border-l-red-500" : "border-l-gray-300"
      )}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Abiertas</p>
              <p className="text-xl font-bold">{stats.open}</p>
            </div>
            <AlertTriangle className={cn("h-6 w-6", stats.open > 0 ? "text-red-500" : "text-gray-400")} />
          </div>
        </CardContent>
      </Card>

      <Card className={cn(
        "border-l-4",
        stats.acknowledged > 0 ? "border-l-yellow-500" : "border-l-gray-300"
      )}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Reconocidas</p>
              <p className="text-xl font-bold">{stats.acknowledged}</p>
            </div>
            <Clock className={cn("h-6 w-6", stats.acknowledged > 0 ? "text-yellow-500" : "text-gray-400")} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-l-4 border-l-green-500">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Resueltas</p>
              <p className="text-xl font-bold">{stats.resolved}</p>
            </div>
            <CheckCircle2 className="h-6 w-6 text-green-500" />
          </div>
        </CardContent>
      </Card>

      <Card className={cn(
        "border-l-4",
        stats.critical > 0 ? "border-l-red-600 bg-red-50" : "border-l-gray-300"
      )}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Criticas</p>
              <p className={cn("text-xl font-bold", stats.critical > 0 && "text-red-600")}>
                {stats.critical}
              </p>
            </div>
            <ShieldAlert className={cn("h-6 w-6", stats.critical > 0 ? "text-red-600" : "text-gray-400")} />
          </div>
        </CardContent>
      </Card>

      <Card className={cn(
        "border-l-4",
        stats.high > 0 ? "border-l-orange-500 bg-orange-50" : "border-l-gray-300"
      )}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Altas</p>
              <p className={cn("text-xl font-bold", stats.high > 0 && "text-orange-600")}>
                {stats.high}
              </p>
            </div>
            <AlertTriangle className={cn("h-6 w-6", stats.high > 0 ? "text-orange-500" : "text-gray-400")} />
          </div>
        </CardContent>
      </Card>

      <Card className={cn(
        "border-l-4",
        stats.slaBreached > 0 ? "border-l-purple-500 bg-purple-50" : "border-l-gray-300"
      )}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">SLA Incump.</p>
              <p className={cn("text-xl font-bold", stats.slaBreached > 0 && "text-purple-600")}>
                {stats.slaBreached}
              </p>
            </div>
            <TrendingDown className={cn("h-6 w-6", stats.slaBreached > 0 ? "text-purple-500" : "text-gray-400")} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
